/**
 * "People they sent": the accounts that signed up through their link.
 */
import { Link, useLocation } from "react-router-dom";
import { UserRound, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";
import { Empty, Pill, Section, dayLabel, usd } from "@/features/command/ui";
import { PLAN_COLORS } from "@/features/accounts/platform-meta";
import type { ReferredAccount } from "./types";

const STATUS_WORDS: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "emerald" },
  trialing: { label: "On a trial", color: "amber" },
  past_due: { label: "Payment failed", color: "rose" },
  unpaid: { label: "Unpaid", color: "rose" },
  canceled: { label: "Cancelled", color: "zinc" },
  cancelled: { label: "Cancelled", color: "zinc" },
  incomplete: { label: "Not finished", color: "zinc" },
};

const planName = (p: string | null) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : "No plan");

export function ReferredCard({ referred, firstName }: { referred: ReferredAccount[]; firstName: string }) {
  const { pathname } = useLocation();
  return (
    <Section title="People they sent" icon={Users} count={referred.length} bodyClassName={referred.length ? "p-0" : undefined}>
      {referred.length === 0 ? (
        <Empty icon={Users} title="Nobody yet">
          When someone signs up with {firstName ? `${firstName}'s` : "their"} link, they show up here.
        </Empty>
      ) : (
        <ul className="divide-y divide-zinc-800/50">
          {referred.map((r) => {
            const st = r.deleted_at ? { label: "Closed", color: "zinc" } : STATUS_WORDS[String(r.subscription_status ?? "")] ?? null;
            const paid = Number(r.lifetime_spend_usd ?? r.revenue_usd ?? 0);
            return (
              <li key={r.id} className={cn("flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between", r.deleted_at && "opacity-60")}>
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={r.name} size="sm" />
                  <div className="min-w-0">
                    <Link to={`/accounts/${r.id}`} state={{ from: pathname }} className="block truncate text-sm font-medium text-zinc-100 hover:text-indigo-300">
                      {r.name || "Unnamed account"}
                    </Link>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                      <span>Signed up {dayLabel(r.platform_created_at, { month: "short", day: "numeric", year: "numeric" })}</span>
                      {r.contact_id && (
                        <Link to={`/contacts/${r.contact_id}`} state={{ from: pathname }} className="inline-flex items-center gap-0.5 font-medium text-indigo-300 hover:text-indigo-200">
                          <UserRound className="h-3 w-3" /> {r.contact_name || "Their contact"}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 pl-11 sm:pl-0">
                  <Pill color={PLAN_COLORS[String(r.plan_code ?? "")] ?? "zinc"}>{planName(r.plan_code)}</Pill>
                  {st && <Pill color={st.color}>{st.label}</Pill>}
                  <span className={cn("w-20 text-right text-sm tabular-nums", paid > 0 ? "font-semibold text-emerald-300" : "text-zinc-500")} title="Paid us so far">
                    {usd(paid)}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {referred.length > 0 && (
        <div className="border-t border-zinc-800/60 px-4 py-2 text-right text-[11px] text-zinc-500">Amounts are what each has paid us so far</div>
      )}
    </Section>
  );
}
